import type { NavItem } from "@dl/web-core-js/shell";
import {
  Boxes,
  Building2,
  Factory,
  FolderOpen,
  Frame,
  Gauge,
  Images,
  KeyRound,
  Layers,
  Link2,
  ListTree,
  MapPin,
  Package,
  PenTool,
  Printer,
  Settings,
  Settings2,
  Share2,
  ShieldCheck,
  ShoppingCart,
  Store,
  Tv,
  Users,
} from "lucide-react";
import { LogicView } from "./views";

/**
 * The nav manifest — the direct replacement for `SideNavigationMenu`.
 *
 * Entries carry the view code that gates them; `visibleNav` removes anything
 * the signed-in account can't reach, so an unauthorized item never renders.
 *
 * The shape is nexpim's, so the two apps read the same way: the dashboard, the
 * main entities, one Production group open by default, a group for where
 * artworks get shown, then Settings for the reference tables and Admin for
 * accounts. Every leaf keeps its Vaadin-era route; only the grouping moved.
 */
export const nav: NavItem[] = [
  { labelKey: "navigation.item.dashboard", to: "/", icon: Gauge, view: LogicView.HOME },
  { labelKey: "navigation.item.artworks", to: "/artworks", icon: Layers, view: LogicView.ARTWORKS },
  { labelKey: "navigation.item.products", to: "/product", icon: Package, view: LogicView.PRODUCT },
  { labelKey: "navigation.item.orders", to: "/orders", icon: ShoppingCart, view: LogicView.ORDERS },
  {
    labelKey: "navigation.item.production",
    icon: Factory,
    defaultOpen: true,
    children: [
      {
        labelKey: "navigation.item.production-jobs",
        to: "/production/job",
        icon: Printer,
        // Either queue opens the page; the switch inside shows what the account may see.
        view: [LogicView.PRODUCTION_JOB, LogicView.PRODUCTION_LEGACY_JOB],
      },
      {
        labelKey: "navigation.item.production-centers",
        to: "/production/center",
        icon: MapPin,
        view: LogicView.PRODUCTION_CENTER,
      },
      {
        labelKey: "navigation.item.print-settings",
        to: "/config/print-settings",
        icon: Settings2,
        view: LogicView.CONFIG_PRINT_SETTINGS,
      },
    ],
  },
  {
    labelKey: "navigation.item.publishing",
    icon: Share2,
    children: [
      {
        labelKey: "navigation.item.collections",
        to: "/collection/collections",
        icon: Boxes,
        view: LogicView.COLLECTION_COLLECTIONS,
      },
      {
        labelKey: "navigation.item.shopify-collections",
        to: "/collection/shopify-collections",
        icon: Store,
        view: LogicView.COLLECTION_SHOPIFY,
      },
      {
        labelKey: "navigation.item.display-collections",
        to: "/collection/display-collections",
        icon: Images,
        view: LogicView.COLLECTION_DISPLAY,
      },
      {
        labelKey: "navigation.item.joliscreen",
        to: "/joliscreen/images",
        icon: Tv,
        view: LogicView.JOLISCREEN_IMAGES,
      },
    ],
  },
  {
    labelKey: "navigation.item.settings",
    icon: Settings,
    children: [
      {
        labelKey: "navigation.item.publishers",
        to: "/config/publisher",
        icon: PenTool,
        // The metadata template lives on the publisher's page now.
        view: [LogicView.CONFIG_PUBLISHER, LogicView.CONFIG_PUBLISHER_METADATA_TEMPLATES],
      },
      {
        labelKey: "navigation.item.catalogs",
        to: "/config/catalog",
        icon: FolderOpen,
        view: LogicView.CONFIG_CATALOG,
      },
      {
        labelKey: "navigation.item.product-categories",
        to: "/product/category",
        icon: ListTree,
        view: LogicView.PRODUCT_CATEGORY,
      },
      {
        labelKey: "navigation.item.decors",
        to: "/config/decor",
        icon: Frame,
        view: LogicView.CONFIG_DECOR,
      },
      {
        labelKey: "navigation.item.stores",
        to: "/config/store",
        icon: Store,
        view: LogicView.CONFIG_STORE,
      },
      {
        labelKey: "navigation.item.clients",
        to: "/config/client",
        icon: Building2,
        view: LogicView.CONFIG_CLIENT,
      },
      {
        labelKey: "navigation.item.sku-match",
        to: "/config/sku-match",
        icon: Link2,
        view: LogicView.CONFIG_SKU_MATCH,
      },
    ],
  },
  {
    labelKey: "navigation.item.admin",
    icon: ShieldCheck,
    children: [
      { labelKey: "navigation.item.users", to: "/users", icon: Users, view: LogicView.ADMIN_ACCOUNTS },
      { labelKey: "navigation.item.app-accounts", to: "/app-accounts", icon: KeyRound, view: LogicView.ADMIN_ACCOUNTS },
    ],
  },
];
